/**
 * SmartSlotEditor Component
 * Modal/popover for editing smart slot criteria
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Check } from 'lucide-react';
import { Range, getTrackBackground } from 'react-range';
import type { SlotCriteria, SmartSlot } from '../../stores/workbench';
import { useWorkbenchStore } from '../../stores/workbench';
import { parseCamelot, getCamelotColor } from '../../lib/camelot';

interface SmartSlotEditorProps {
  slot: SmartSlot;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * All possible Camelot keys for selection
 */
const ALL_CAMELOT_KEYS = Array.from({ length: 12 }, (_, i) => {
  const num = i + 1;
  return [`${num}A`, `${num}B`];
}).flat();

/**
 * Dual-thumb range slider component using react-range
 */
function RangeSlider({
  label,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
}: {
  label: string;
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatValue?: (val: number) => string;
}) {
  const format = formatValue || ((v: number) => v.toString());
  const values = value;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-zinc-300">{label}</label>
        <span className="text-xs text-zinc-500">
          {format(values[0])} - {format(values[1])}
        </span>
      </div>

      <Range
        values={values}
        step={step}
        min={min}
        max={max}
        onChange={(newValues) => {
          if (newValues[0] !== undefined && newValues[1] !== undefined) {
            onChange([newValues[0], newValues[1]]);
          }
        }}
        renderTrack={({ props, children }) => (
          <div
            onMouseDown={props.onMouseDown}
            onTouchStart={props.onTouchStart}
            style={{
              ...props.style,
              height: '36px',
              display: 'flex',
              width: '100%',
            }}
          >
            <div
              ref={props.ref}
              style={{
                height: '6px',
                width: '100%',
                borderRadius: '4px',
                background: getTrackBackground({
                  values,
                  colors: ['#27272a', '#10b981', '#27272a'],
                  min,
                  max,
                }),
                alignSelf: 'center',
              }}
            >
              {children}
            </div>
          </div>
        )}
        renderThumb={({ props, isDragged }) => (
          <div
            {...props}
            key={props.key}
            style={{
              ...props.style,
              height: '20px',
              width: '20px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: isDragged
                ? '0 0 0 4px rgba(16, 185, 129, 0.2)'
                : '0 2px 6px rgba(0, 0, 0, 0.3)',
              outline: 'none',
            }}
          >
            <div
              style={{
                height: '10px',
                width: '10px',
                borderRadius: '50%',
                backgroundColor: isDragged ? '#ffffff' : '#d1fae5',
              }}
            />
          </div>
        )}
      />
    </div>
  );
}

export function SmartSlotEditor({ slot, isOpen, onClose }: SmartSlotEditorProps) {
  const { updateSmartSlot } = useWorkbenchStore();

  // Local state for editing
  const [criteria, setCriteria] = useState<SlotCriteria>(slot.criteria);

  // Sync with slot changes
  useEffect(() => {
    setCriteria(slot.criteria);
  }, [slot.criteria]);

  // Handle save
  const handleSave = () => {
    updateSmartSlot(slot.id, { criteria });
    onClose();
  };

  // Toggle key selection
  const toggleKey = (key: string) => {
    const current = criteria.keys || [];
    const updated = current.includes(key)
      ? current.filter(k => k !== key)
      : [...current, key];
    setCriteria({ ...criteria, keys: updated });
  };

  // Clear all keys
  const clearKeys = () => {
    setCriteria({ ...criteria, keys: [] });
  };

  // Select all keys
  const selectAllKeys = () => {
    setCriteria({ ...criteria, keys: [...ALL_CAMELOT_KEYS] });
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[101] p-4">
        <div
          className="bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Sparkles className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-100">
                  Configure Smart Slot
                </h2>
                <p className="text-sm text-zinc-500">
                  Set criteria for AI-powered track matching
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* BPM Range */}
            <RangeSlider
              label="BPM Range"
              min={60}
              max={200}
              step={1}
              value={[
                criteria.bpm?.min ?? 100,
                criteria.bpm?.max ?? 140,
              ]}
              onChange={([min, max]) =>
                setCriteria({ ...criteria, bpm: { min, max } })
              }
              formatValue={(v) => `${v} BPM`}
            />

            {/* Camelot Keys */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-zinc-300">
                  Compatible Keys
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={clearKeys}
                    className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={selectAllKeys}
                    className="text-xs text-emerald-500 hover:text-emerald-400 transition-colors"
                  >
                    Select All
                  </button>
                </div>
              </div>

              {/* Key Grid */}
              <div className="grid grid-cols-12 gap-2">
                {ALL_CAMELOT_KEYS.map((key) => {
                  const isSelected = (criteria.keys || []).includes(key);
                  const parsed = parseCamelot(key);
                  const colorClass = getCamelotColor(key);

                  return (
                    <button
                      key={key}
                      onClick={() => toggleKey(key)}
                      className={`
                        relative p-2 rounded-lg border transition-all text-xs font-medium
                        ${isSelected
                          ? `${colorClass} border-current`
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-500 hover:border-zinc-600'
                        }
                      `}
                      title={`${key} - ${parsed?.letter === 'A' ? 'Minor' : 'Major'}`}
                    >
                      {isSelected && (
                        <Check className="absolute top-0.5 right-0.5 w-3 h-3" />
                      )}
                      {key}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Energy Range */}
            <RangeSlider
              label="Energy"
              min={0}
              max={1}
              step={0.05}
              value={[
                criteria.energy?.min ?? 0.3,
                criteria.energy?.max ?? 0.8,
              ]}
              onChange={([min, max]) =>
                setCriteria({ ...criteria, energy: { min, max } })
              }
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />

            {/* Valence Range */}
            <RangeSlider
              label="Valence (Mood)"
              min={0}
              max={1}
              step={0.05}
              value={[
                criteria.valence?.min ?? 0.2,
                criteria.valence?.max ?? 0.9,
              ]}
              onChange={([min, max]) =>
                setCriteria({ ...criteria, valence: { min, max } })
              }
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />

            {/* Danceability Range */}
            <RangeSlider
              label="Danceability"
              min={0}
              max={1}
              step={0.05}
              value={[
                criteria.danceability?.min ?? 0.3,
                criteria.danceability?.max ?? 0.9,
              ]}
              onChange={([min, max]) =>
                setCriteria({ ...criteria, danceability: { min, max } })
              }
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-zinc-800 bg-zinc-900/50">
            <p className="text-xs text-zinc-500">
              Changes will update matching recommendations
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors text-sm font-medium flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Save & Find Matches
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
